import { WorkOrder } from "@shared/models/work-order";
import type { Marker } from "leaflet";
import {
  ROUTE_PLANNER_POLYLINE_COLOR,
  ROUTE_PLANNER_POLYLINE_SHAPE_OPTIONS, RouteNote,
  RoutePlanningBase,
  RoutePlanningRoute
} from "@shared/models/route-planning";
import { TranslateService } from "@ngx-translate/core";
import { StatusDefinitions } from "@shared/models/status";

declare const L: any;

/**
 * Helper functions for handling leaflet map tasks
 */
export class MapUtils {

  constructor(
  ) {
  }

  /**
   * Creates a border around polyline for highlighting
   * @param selectedPolyline
   * @param highlightColor
   * @param map
   */
  public static createPolylineHighlight(
    selectedPolyline: L.Polyline,
    highlightColor: string,
    map?: L.Map,
  ): L.Polyline {
    const polyline = L.polyline(
      selectedPolyline.getLatLngs(),
      {
        ...selectedPolyline.options,
        color: MapUtils.isWhiteColor(selectedPolyline.options?.color) && MapUtils.isWhiteColor(highlightColor)
          ? 'black'
          : highlightColor,
        weight: selectedPolyline.options?.weight ? selectedPolyline.options?.weight + 2 : 7,
      }
    );

    if (map) polyline.addTo(map);

    selectedPolyline.bringToFront();
    return polyline;
  }

  public static isWhiteColor(colorHex: string | undefined): boolean {
    if (!colorHex) return false;
    const color = colorHex.toLowerCase();
    return color === 'white' || color === '#fff' || color === '#ffffff';
  }

  public static highlightMarker(
    color: string,
    markersMap: Map<number, Marker>,
    workOrderId: number,
    highlightedMarkers?: { id: number, color: string, weight: number }[],
  ): void {
    const marker = markersMap.get(workOrderId);
    if (!marker) return;

    const options = marker.options as any;

    if (highlightedMarkers) {
      highlightedMarkers.push({
        id: workOrderId,
        color: options.color,
        weight: options.weight,
      });
    }

    (marker as any).setStyle({
      color: color,
      weight: 2,
    });
  }

  public static resetMarkerHighlights(
    markersMap: Map<number, Marker>,
    highlightedMarkers: { id: number, color: string, weight: number }[]
  ): void {
    for (const markerOptions of highlightedMarkers) {
      const marker = markersMap.get(markerOptions.id);
      (marker as any).setStyle({
        color: markerOptions.color,
        weight: markerOptions.weight,
      });
    }

    highlightedMarkers = [];
  }

  public static goToLocation(map: L.Map, lat: number, lng: number, zoom: number = 17): void {
    if (map) {
      map.flyTo([lat, lng], zoom, {
        animate: true,
        duration: 1
      });
    }
  }

  public static getRelativeMarkerSize(baseValue: any, zoomLevel: number, customerId): [number, number] {
    switch (baseValue) {
        case '1P75':
          baseValue = 75
          break;
        case '1P50':
          baseValue = 50
          break;
        case '1P25':
          baseValue = 25
          break;
        case undefined:
          baseValue = 25
          break;
        case null:
          baseValue = 25
          break;
        default:
          baseValue = baseValue
          break;
      }
    if (customerId === 2) {
      let zoomLevelBreak = 18
      if (zoomLevel < 13) return [60, 40]
      if (zoomLevel >= 13 && zoomLevel < 16) {
        const height = 25 * zoomLevel * 0.005
        const width = 75 * zoomLevel * 0.005
        return [width, height];
      } else if (zoomLevel > zoomLevelBreak) {
        const height = 25 * zoomLevel * 0.025
        const width = 75 * zoomLevel * 0.025
        return [width, height];
      } else {
        const height = 25 * zoomLevel * 0.005
        const width = 75 * zoomLevel * 0.005
        return [width, height];
      }
    } else if (customerId === 4) {
      if (zoomLevel < 11) return [baseValue, 15];

      // Define multipliers for each zoom level
      const levelMultipliers: { [key: number]: number } = {
        11: 0.001,
        12: 0.0025,
        13: 0.005,
        14: 0.01,
        15: 0.03,
        16: 0.05,
        17: 0.1,
        18: 0.2,
        19: 0.3,
        20: 0.4,
        21: 0.5,
        22: 0.6,
        23: 0.7,
        24: 0.8,
        25: 0.9
      };

      let heightMultiplier = levelMultipliers[zoomLevel];
      let widthMultiplier = 0.025;

      if (zoomLevel >= 11 && zoomLevel < 14) {
        widthMultiplier = 0.0025;
      } else if (zoomLevel >= 14 && zoomLevel < 17) {
        widthMultiplier = 0.005;
      } else if (zoomLevel === 17) {
        widthMultiplier = 0.01;
      } else if (zoomLevel > 17) {
        widthMultiplier = 0.020;
      }

      const height = baseValue * zoomLevel * heightMultiplier;
      const width = 50 * zoomLevel * widthMultiplier;
      return [width, height];
    } else {
      let zoomLevelBreak = 19
      if (customerId === 3) zoomLevelBreak = 17
      if (zoomLevel < 13) return [40, 60]
      else if (zoomLevel >= 13 && zoomLevel < 16) {
        const height = baseValue * zoomLevel * 0.01
        const width = 15 * zoomLevel * 0.01
        return [width, height];
      } else if (zoomLevel > zoomLevelBreak) {
        const height = baseValue * zoomLevel * 0.065
        const width = 20 * zoomLevel * 0.065
        return [width, height];
      } else {
        const height = baseValue * zoomLevel * 0.025
        const width = 15 * zoomLevel * 0.025
        return [width, height];
      }
    }
  }

  public static getPowerlineMarker(workOrder: WorkOrder, map?: L.Map): L.Marker {
    const point = new L.LatLng(workOrder.latitude, workOrder.longitude);
    const customIcon = L.icon({
      iconUrl: this.getPowerlineIcon(workOrder),
      iconSize: [24, 24],
      iconAnchor: [12, 12],
      popupAnchor: [0, -20]
    });

    const marker = L.marker(point, { icon: customIcon });
    if (map) marker.addTo(map);

    return marker;
  }

  public static getPowerlineIcon(workOrder: WorkOrder): string {
    return workOrder.anchored
      ? workOrder.tightening
        ? '/assets/icons/powerlines/h_k.png'
        : '/assets/icons/powerlines/h.png'
      : workOrder.tightening
        ? '/assets/icons/powerlines/v_k.png'
        : '/assets/icons/powerlines/v.png';
  }

  public static getPowerlineCustomMarker(
      workOrder: WorkOrder,
      colorHex: string,
      size: number,
      map: L.Map,
  ): L.Marker {
    const point = new L.LatLng(workOrder.latitude, workOrder.longitude);
    const svg_h = `
      <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${size}" height="${size}" viewBox="0 0 40 40">
        <defs>
          <clipPath id="clip-Harustettu_kannatuspylväs_purple_1">
            <rect width="40" height="40"/>
          </clipPath>
        </defs>
        <g id="Harustettu_kannatuspylväs_purple_1" data-name="Harustettu, kannatuspylväs (purple) – 1" clip-path="url(#clip-Harustettu_kannatuspylväs_purple_1)">
          <g id="Group_5210" data-name="Group 5210" transform="translate(-1789.164 -752.353)">
            <path id="Path_4058" data-name="Path 4058" d="M20.685,5.832V21.886h-3.22V15.055H10.588v6.831H7.368V5.832h3.22v6.6h6.877v-6.6Z" transform="translate(1795.139 759.092)" fill="${colorHex}"/>
          </g>
        </g>
      </svg>
    `;

    const svg_h_k = `
      <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${size}" height="${size}" viewBox="0 0 40 40">
        <defs>
          <clipPath id="clip-Harustettu_kiristyspylväs_purple_1">
            <rect width="40" height="40"/>
          </clipPath>
        </defs>
        <g id="Harustettu_kiristyspylväs_purple_1" data-name="Harustettu, kiristyspylväs (purple) – 1" clip-path="url(#clip-Harustettu_kiristyspylväs_purple_1)">
          <g id="Group_5210" data-name="Group 5210" transform="translate(-1789.164 -752.353)">
            <path id="Path_4058" data-name="Path 4058" d="M20.685,5.832V21.886h-3.22V15.055H10.588v6.831H7.368V5.832h3.22v6.6h6.877v-6.6Z" transform="translate(1795.139 759.092)" fill="${colorHex}"/>
            <path id="Ellipse_1274" data-name="Ellipse 1274" d="M17.25,2.875A14.375,14.375,0,1,0,31.626,17.25,14.392,14.392,0,0,0,17.25,2.875M17.25,0A17.25,17.25,0,1,1,0,17.25,17.25,17.25,0,0,1,17.25,0Z" transform="translate(1791.914 755.103)" fill="${colorHex}"/>
          </g>
        </g>
      </svg>
    `;

    const svg_v = `
      <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${size}" height="${size}" viewBox="0 0 40 40">
        <defs>
          <clipPath id="clip-Vapaasti_kannatuspylväs_purple_1">
            <rect width="40" height="40"/>
          </clipPath>
        </defs>
        <g id="Vapaasti_kannatuspylväs_purple_1" data-name="Vapaasti, kannatuspylväs (purple) – 1" clip-path="url(#clip-Vapaasti_kannatuspylväs_purple_1)">
          <g id="Group_5209" data-name="Group 5209" transform="translate(-1202.651 -433.22)">
            <path id="Path_4061" data-name="Path 4061" d="M22.222,5.832,16.334,21.886h-3.91L6.536,5.832h3.45L14.4,18.6,18.8,5.832Z" transform="translate(1208.26 439.959)" fill="${colorHex}"/>
          </g>
        </g>
      </svg>
    `;

    const svg_v_k = `
      <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${size}" height="${size}" viewBox="0 0 40 40">
        <defs>
          <clipPath id="clip-Vapaasti_kiristyspylväs_purple_1">
            <rect width="40" height="40"/>
          </clipPath>
        </defs>
        <g id="Vapaasti_kiristyspylväs_purple_1" data-name="Vapaasti, kiristyspylväs (purple) – 1" clip-path="url(#clip-Vapaasti_kiristyspylväs_purple_1)">
          <g id="Group_5209" data-name="Group 5209" transform="translate(-1202.651 -433.22)">
            <path id="Path_4061" data-name="Path 4061" d="M22.222,5.832,16.334,21.886h-3.91L6.536,5.832h3.45L14.4,18.6,18.8,5.832Z" transform="translate(1208.26 439.959)" fill="${colorHex}"/>
            <path id="Ellipse_1274" data-name="Ellipse 1274" d="M17.25,2.875A14.375,14.375,0,1,0,31.626,17.25,14.392,14.392,0,0,0,17.25,2.875M17.25,0A17.25,17.25,0,1,1,0,17.25,17.25,17.25,0,0,1,17.25,0Z" transform="translate(1205.4 435.97)" fill="${colorHex}"/>
          </g>
        </g>
      </svg>
    `;

    const borderSize = size + 5;
    const svg_border = `
      <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${borderSize}" height="${borderSize}" viewBox="0 0 40 40">
        <defs>
          <clipPath id="clip-Vapaasti_kiristyspylväs_purple_1">
            <rect width="40" height="40"/>
          </clipPath>
        </defs>
        <g id="Vapaasti_kiristyspylväs_purple_1" data-name="Vapaasti, kiristyspylväs (purple) – 1" clip-path="url(#clip-Vapaasti_kiristyspylväs_purple_1)">
           <g transform="translate(-1202.651 -433.22)">
              <path d="M17.25,1.5A15.75,15.75,0,1,0,33,17.25,15.768,15.768,0,0,0,17.25,1.5
                       M17.25,0A17.25,17.25,0,1,1,0,17.25,17.25,17.25,0,0,1,17.25,0Z"
                    transform="translate(1205.4 435.97)"
                    fill="${workOrder.bordercolor}"/>
           </g>
        </g>
      </svg>
    `;

    // deviation
    if (workOrder.report_type === 3) {
      const borderIcon = L.divIcon({
        html: svg_border,
        className: '',
        iconSize: [borderSize, borderSize],
        iconAnchor: [borderSize / 2, borderSize / 2],
        popupAnchor: [0, -20]
      });

      L.marker(point, {icon: borderIcon}).addTo(map);
    }

    const customIcon = L.divIcon({
      html: workOrder.anchored
          ? workOrder.tightening
              ? svg_h_k
              : svg_h
          : workOrder.tightening
              ? svg_v_k
              : svg_v,
      className: '',
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
      popupAnchor: [0, -20]
    });

    return L.marker(point, { icon: customIcon }).addTo(map);
  }

  public static createBaseIcon(base: RoutePlanningBase,
                         baseMarkers: Map<number, Marker<L.Marker>>,
                         map: L.Map,
                         translateService: TranslateService,
                         showLabel?: boolean,
                         isLabelPermanent?: boolean,
                         deleteCallback?: (baseId: number) => void,
  ): L.Marker {
    const point = new L.LatLng(base.coordinates.lat, base.coordinates.lng);
    const customIcon = L.icon({
      iconUrl: '/assets/icons/powerlines/base.png',
      iconSize: [24, 24],
      iconAnchor: [12, 12],
      popupAnchor: [0, -20]
    });

    const marker = L.marker(point, { icon: customIcon }).addTo(map);
    baseMarkers.set(base.id, marker);
    const title = base.identifier
      ? base.identifier
      : translateService.instant('planner.routePlanning.base') + ' #' + base.id;

    if (!showLabel) return marker;

    const tooltip = marker.bindTooltip(`
      <div class="title">${title}</div>
      ${base.notes ? '<div class="notes">' + base.notes + '</div>' : ''}
    `, {
      permanent: isLabelPermanent,
      direction: 'bottom',
      offset: [0, 10]
    }).getTooltip();

    // needed to adjust the initial tooltip position because of white-space: break-spaces
    if (tooltip) tooltip.update();

    if (!deleteCallback) return marker;

    const markerLayer = marker.bindPopup(`
      <div class="title">${translateService.instant('planner.routePlanning.base')} #${base.id}</div>
      ${base.notes ? '<div class="notes">' + base.notes + '</div>' : ''}
      <button class="btn btn-delete btn-danger" id="deleteBase">
        ${translateService.instant('basic.delete')}
      </button>
    `);

    markerLayer.on('popupopen', () => {
      const button = document.getElementById('deleteBase');
      if (button) button.addEventListener('click', () => deleteCallback(base.id));
    });

    return marker;
  }

  public static createRoutePolyline(map: L.Map, route: RoutePlanningRoute, tooltipHtml?: string): L.Polyline | null {
    if (route?.coordinates?.length < 2) return null;

    const options = ROUTE_PLANNER_POLYLINE_SHAPE_OPTIONS;
    options.color = route.color || ROUTE_PLANNER_POLYLINE_COLOR;
    options.originalColor = route.color || ROUTE_PLANNER_POLYLINE_COLOR;

    const polylineLayer = L.polyline(route?.coordinates, options);
    if (tooltipHtml) polylineLayer.bindTooltip(tooltipHtml, {direction: 'bottom'});

    polylineLayer.routeId = route.id;
    map.addLayer(polylineLayer);
    return polylineLayer;
  }

  public static setPolylineTooltip(
    map: L.Map,
    tooltip: L.Tooltip,
    polylineLayer: L.Polyline,
    html: string,
  ): void {
    polylineLayer.on('mouseover', (e: L.LeafletMouseEvent) => {
      tooltip.setContent(html);
      tooltip.setLatLng(e.latlng);
      map.openTooltip(tooltip);
    });

    polylineLayer.on('mousemove', (e: L.LeafletMouseEvent) => {
      tooltip.setLatLng(e.latlng);
    });

    polylineLayer.on('mouseout', () => {
      map.closeTooltip(tooltip);
    });
  }

  public static statusCompareJson(definitionJson: string, status: StatusDefinitions): boolean {
    const definition = JSON.parse(definitionJson)?.['en'];
    return MapUtils.statusCompare(definition, status);
  }

  public static statusCompare(definition: string, status: StatusDefinitions): boolean {
    const definitionUpper = definition?.split(' ')?.join('_')?.toUpperCase();
    return definitionUpper === status;
  }

  /**
   * Get scaled border weight based on zoom
   * @param zoom current zoom level
   * @param zoomBreak on what zoom level the border should be fully visible?
   * @param scaleFactor 0 - 1.0, how quickly to decrease the border weight
   */
  public static getBorderWeight(zoom: number, zoomBreak: number, scaleFactor: number): number {
    if (zoom >= zoomBreak) return 1;

    let weight = 1 - (zoomBreak - zoom) * scaleFactor;
    if (weight <= 0.1) weight = 0.1;

    return weight;
  }

  /**
   * Creates a floating triangle arrow marker
   * @param lat
   * @param lng
   */
  public static createPointerMarker(lat: number, lng: number): L.Marker | null {
    if (!lat || !lng) return null;

    const html = `
      <div class="floating"
      style="
        width: 0;
        height: 0;
        border-left: 8px solid transparent;
        border-right: 8px solid transparent;
        border-top: 16px solid red;
        transform: translate(-50%, -50%);
      "></div>
    `;

    const icon = L.divIcon({
      className: '',
      html: html,
      iconSize: [10, 10],
      iconAnchor: [0, 10],
    });

    return L.marker([lat, lng], { icon });
  }

  public static safeSplit(source: string, separator: string): number[] {
    return source?.split(separator)?.map(item => +item) || [];
  }

  /**
   * Calculate the total length of a route in meters
   * Uses Leaflet's distanceTo() which implements Haversine formula
   * @param coordinates Array of lat/lng coordinate pairs
   * @returns Total distance in meters
   */
  public static calculateRouteLength(coordinates: { lat: number; lng: number }[]): number {
    if (!coordinates || coordinates.length < 2) return 0;

    let totalDistance = 0;
    for (let i = 0; i < coordinates.length - 1; i++) {
      const point1 = L.latLng(coordinates[i].lat, coordinates[i].lng);
      const point2 = L.latLng(coordinates[i + 1].lat, coordinates[i + 1].lng);
      totalDistance += point1.distanceTo(point2);
    }

    return totalDistance;
  }

  /**
   * Format distance for display (meters or kilometers)
   * @param meters Distance in meters
   * @returns Formatted string with unit
   */
  public static formatRouteLength(meters: number): string {
    if (meters < 1000) {
      return `${Math.round(meters)} m`;
    }

    return `${(meters / 1000).toFixed(2)} km`;
  }

  public static createRouteNoteIcon(routeNote: RouteNote): L.Marker {
    const point = new L.LatLng(routeNote.coordinates.lat, routeNote.coordinates.lng);

    const html = `
    <div style="
      width: 26px;
      height: 26px;
      border: 3px solid black;
      border-radius: 50%;
      background-color: red;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: bold;
      font-size: 16px;
    ">
      !
    </div>
  `;

    const icon = L.divIcon({
      html,
      className: '',
      iconSize: [26, 26],
      iconAnchor: [13, 13],
    });

    return L.marker(point, { icon });
  }
}
